import React from 'react';
import { calc, computeTimeline, evaluateTimeline, fmtChf, fmtNum, generateConfigCode } from './calc.js';
import { t } from './i18n.js';
import { AdvisoryCard } from './page3.jsx';

// ---- localised label helpers (page 4 review) ---------------------------------
const _platformLabel    = (id) => id ? t(`platform_types.${id}`) : '—';
const _projectLabel     = (id) => id ? t(`project_kinds.${id}.title`) : '—';
const _companyLabel     = (id) => id ? t(`company_kinds.${id}.title`) : '—';
const _moduleLabel      = (id) => id ? t(`modules.module.${id}`) : '';
const _hilfsLabel       = (id) => id ? t(`modules.hilfs.${id}`) : '';
const _kiLabel          = (id) => id ? t(`modules.ki.${id}`) : '';
const _stackcmsLabel    = (id) => id ? t(`modules.stackCms.${id}`) : '';
const _sensLabel        = (id) => id ? t(`p2.sensitivity_${id}_label`) : '—';
const _availLabel       = (id) => {
  const map = { standard: 'availability_standard_title', 'low-downtime': 'availability_low_title', 'full-ha': 'availability_full_title' };
  return id && map[id] ? t(`p2.${map[id]}`) : '—';
};
const _tenantLabel      = (id) => {
  const map = { single: 'tenant_single_title', 'multi-mid': 'tenant_mid_title', 'multi-many': 'tenant_many_title' };
  return id && map[id] ? t(`p2.${map[id]}`) : '—';
};
const _userLabel        = (id) => id ? t(`p2.users_${id}_label`) : '—';
const _peakLabel        = (id) => ({ '50': '< 50', '500': '< 500', '5k': '< 5’000', '5k+': '≥ 5’000' })[id] || '—';
const _residencyLabel   = (id) => id ? t(`p2.data_residency_${id}_label`) : '—';
const _performLabel     = (id) => id ? t(`p1.perf_${id}_label`) : '—';
const _auditLabel       = (id) => id ? t(`p1.audit_${id}_label`) : '—';
const _langLabel        = (id) => id ? t(`p1.languages_${id}`) : '—';
const _cxLabel          = (id) => id ? t(`p1.custom_complexity_${id}_label`) : '—';

const Result = ({ state, setState, calc, goTo, openDrawer }) => {
  const r = calc(state);
  const { pre, p1, p2, p3 } = state;
  const assessment = state?.ki_metadata?.assessment;
  const setP3 = (patch) => setState(s => ({ ...s, p3: { ...s.p3, ...patch } }));

  const moduleNames   = (p1.modules  || []).map(_moduleLabel).filter(Boolean);
  const hilfsNames    = (p1.hilfs    || []).map(_hilfsLabel).filter(Boolean);
  const kiNames       = (p1.ai       || []).map(_kiLabel).filter(Boolean);
  const stackcmsNames = (p1.stackcms || []).map(_stackcmsLabel).filter(Boolean);
  const platformLabel = _platformLabel(pre.platformType);
  const projectLabel  = _projectLabel(pre.projectKind);
  const companyLabel  = _companyLabel(pre.companyKind);

  // Range positioning: where the recommendation sits inside [low..high] (≈50%)
  const range = r.confidenceHigh - r.confidenceLow || 1;
  const pinPct = Math.max(2, Math.min(98, Math.round(((r.recommendedBudget - r.confidenceLow) / range) * 100)));

  // Top 3 drivers normalised to %
  const driverMax = Math.max(1, ...r.allDrivers.map(d => d.weight));
  const drivers3 = r.allDrivers.slice(0, 3).map(d => ({
    ...d,
    pct: Math.max(0, Math.round((d.weight / driverMax) * 100)),
  }));

  const cfgCode = state.configCode || generateConfigCode(state);
  const yearsLabel = p3.financingYears === 1
    ? t('p3.financing_year_one',  { value: p3.financingYears })
    : t('p3.financing_year_more', { value: p3.financingYears });

  return (
    <div className="va" style={{ maxWidth: 980, margin: '0 auto' }}>
      <div className="va__shell">

        {/* HERO ============================================================ */}
        <header className="va__hero">
          <div className="va__hero-eyebrow">{t('result.v4_hero_eyebrow')}</div>
          <h1 className="va__hero-title">{pre.title || t('result.default_title')}</h1>
          <p className="va__hero-sub">
            {platformLabel} · {projectLabel} · {companyLabel}
          </p>

          <div className="va__big-number">
            <span className="va__big-label">{t('result.v4_hero_recommended_label')}</span>
            <span className="va__big-value">{fmtChf(Number.isFinite(p3.budget) ? p3.budget : r.recommendedBudget)}</span>
          </div>

          <div className="va__range">
            <div className="va__range-track">
              <div className="va__range-fill"></div>
              <div className="va__range-marker" style={{ left: pinPct + '%' }}>
                <div className="va__range-marker-pin"></div>
                <div className="va__range-marker-label">
                  <strong>{fmtChf(r.recommendedBudget)}</strong><br/>
                  {t('p3.advisory_recommended')}
                </div>
              </div>
            </div>
            <div className="va__range-axis">
              <span>{fmtChf(r.confidenceLow)}</span>
              <span className="va__range-axis-mid">{t('result.v4_range_axis_mid', { pct: Math.round(r.bandWidth * 100) })}</span>
              <span>{fmtChf(r.confidenceHigh)}</span>
            </div>
          </div>

          <div className="va__kpi-row">
            <div className="va__kpi">
              <span className="va__kpi-cap">{t('result.v4_kpi_effort_cap')}</span>
              <span className="va__kpi-val">{t('result.v4_kpi_effort_val', { pt: fmtNum(r.personentage) })}</span>
              <span className="va__kpi-sub">{t('result.v4_kpi_effort_sub', { hours: fmtNum(r.hours) })}</span>
            </div>
            <div className="va__kpi">
              <span className="va__kpi-cap">{t('result.v4_kpi_complexity_cap')}</span>
              <span className="va__kpi-val">{r.cxBand}</span>
              <span className="va__kpi-sub">{t('result.v4_kpi_complexity_sub', { value: r.cxIndex })}</span>
            </div>
            <div className="va__kpi">
              <span className="va__kpi-cap">{t('result.v4_kpi_tco5_cap')}</span>
              <span className="va__kpi-val">{fmtChf(r.tco5)}</span>
              <span className="va__kpi-sub">{t('result.v4_kpi_tco5_sub')}</span>
            </div>
          </div>
        </header>

        {/* BODY ============================================================ */}
        <div className="va__body">

          {/* Intro row: Yearly cost + Decider questions */}
          <div className="va__intro-row">
            <div className="va__yearly-stack">
              {(() => {
                const buildBudget = r.chosenBudget;
                const downPaymentAmount = buildBudget * (p3.downPayment / 100);
                const hasInstallments = p3.downPayment < 100 && p3.financingYears > 0;
                const installmentAmount = hasInstallments ? (buildBudget - downPaymentAmount) / p3.financingYears : 0;
                return (
                  <div className="va__yearly-block">
                    <div className="va__yearly-head">
                      <span className="va__yearly-pill">{t('result.v4_build_pill')}</span>
                    </div>
                    <div className="va__yearly-row">
                      <span>{t('result.v4_build_downpayment', { pct: p3.downPayment })}</span>
                      <span className="amt">{fmtChf(downPaymentAmount)}</span>
                    </div>
                    {hasInstallments && (
                      <div className="va__yearly-row">
                        <span>{t('result.v4_build_installments', { years: p3.financingYears })}</span>
                        <span className="amt">{fmtChf(installmentAmount)}</span>
                      </div>
                    )}
                    <div className="va__yearly-row va__yearly-row--total">
                      <span>{t('result.v4_build_budget_total')}</span>
                      <span className="amt">{fmtChf(buildBudget)}</span>
                    </div>
                  </div>
                );
              })()}

              <div className="va__yearly-block">
                <div className="va__yearly-head">
                  <span className="va__yearly-pill">{t('result.v4_yearly_pill')}</span>
                </div>
                <div className="va__yearly-row"><span>{t('result.v4_yearly_lic')}</span><span className="amt">{fmtChf(r.yearlyLicense)}</span></div>
                <div className="va__yearly-row"><span>{t('result.v4_yearly_serv')}</span><span className="amt">{fmtChf(r.yearlyService)}</span></div>
                <div className="va__yearly-row"><span>{t('result.v4_yearly_host')}</span><span className="amt">{fmtChf(r.yearlyHosting)}</span></div>
                <div className="va__yearly-row"><span>{t('result.v4_yearly_growth', { pct: p3.growthBudget })}</span><span className="amt">{fmtChf(r.yearlyGrowth)}</span></div>
                {r.yearlyOps > 0 && (
                  <div className="va__yearly-row"><span>{t('result.v4_yearly_ops')}</span><span className="amt">{fmtChf(r.yearlyOps)}</span></div>
                )}
                <div className="va__yearly-row va__yearly-row--total">
                  <span>{t('result.v4_yearly_total')}</span>
                  <span className="amt">{fmtChf(r.yearlyRunning)}</span>
                </div>
              </div>
            </div>

            <div className="va__assessment">
              <div className="va__assessment-eyebrow">
                <i className="fa-solid fa-wand-magic-sparkles"></i>
                {t('result.assessment_eyebrow')}
              </div>
              <h2 className="va__assessment-title">{t('result.assessment_title')}</h2>

              {assessment ? (
                <>
                  <div className="va__assessment-block">
                    <div className="va__assessment-label">{t('result.assessment_info_quality_label')}</div>
                    <p className="va__assessment-text">{assessment.informationsqualitaet}</p>
                  </div>

                  <div className="va__assessment-block">
                    <div className="va__assessment-label">{t('result.assessment_tech_fit_label')}</div>
                    <div className="va__assessment-rating">
                      {t(`result.assessment_rating.${assessment.technologieFit?.rating || 'hoch'}`)}
                    </div>
                    <p className="va__assessment-text">{assessment.technologieFit?.text}</p>
                  </div>
                </>
              ) : (
                <p className="va__assessment-text">{t('result.assessment_empty')}</p>
              )}
            </div>
          </div>

          {/* Umsetzungszeitraum — Wunsch vs realistisch */}
          {(() => {
            const wish     = Number.isFinite(pre.timelineMonths) ? pre.timelineMonths : 6;
            const tline    = computeTimeline(r.recommendedBudget);
            const evalT    = evaluateTimeline(wish, tline.realisticMin, tline.realisticMax);
            const axisMin  = 6;
            const axisMax  = 18;
            const span     = axisMax - axisMin;
            const pctOf    = (m) => Math.max(0, Math.min(100, ((m - axisMin) / span) * 100));
            const wishPct  = pctOf(wish);
            const bandLeft = pctOf(tline.realisticMin);
            const bandRight= pctOf(tline.realisticMax);
            const accent   = evalT.status === 'ok' ? 'var(--success-50, #00C389)'
                            : evalT.status === 'tight' ? 'var(--warning-50, #FFCA57)'
                            : 'var(--warning-60, #FF966A)';
            const rangeText = tline.realisticMin === tline.realisticMax
              ? t('result.timeline_range_single', { months: tline.realisticMin })
              : t('result.timeline_range_span',   { min: tline.realisticMin, max: tline.realisticMax });
            return (
              <div className="va__timeline" style={{ marginBottom: 28, padding: '12px 28px 32px', border: '1px solid var(--border-color-soft)', borderRadius: 12, background: 'var(--surface-1, transparent)' }}>
                <div className="between" style={{ alignItems: 'flex-start', marginBottom: 16 }}>
                  <div>
                    <span className="block__cat">{t('result.timeline_cat')}</span>
                    <h3 className="va__sect-title" style={{ margin: '4px 0 0' }}>{t('result.timeline_title')}</h3>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 12, color: 'var(--text-color-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('result.timeline_wish_label')}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: accent }}>{t(wish === 1 ? 'preStep.timeline_month_one' : 'preStep.timeline_month_more', { value: wish })}</div>
                  </div>
                </div>

                <div style={{ position: 'relative', height: 56, marginBottom: 12 }}>
                  <div style={{ position: 'absolute', left: 0, right: 0, top: 22, height: 6, borderRadius: 3, background: 'var(--bg-color-soft, #E4E5EA)' }}/>
                  <div style={{ position: 'absolute', top: 22, height: 6, borderRadius: 3, left: bandLeft + '%', width: Math.max(2, bandRight - bandLeft) + '%', background: 'var(--primary-50, #02FFA1)', opacity: 0.35 }}/>
                  <div style={{ position: 'absolute', top: 14, left: bandLeft + '%', width: 2, height: 22, background: 'var(--primary-50, #02FFA1)' }}/>
                  <div style={{ position: 'absolute', top: 14, left: bandRight + '%', width: 2, height: 22, background: 'var(--primary-50, #02FFA1)' }}/>
                  <div style={{ position: 'absolute', top: 8, left: `calc(${wishPct}% - 8px)`, width: 16, height: 34, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ width: 14, height: 14, borderRadius: 7, background: accent, border: '3px solid var(--global-background, #fff)', boxShadow: '0 0 0 1px ' + accent }}/>
                  </div>
                  <div style={{ position: 'absolute', top: 40, left: 0, right: 0, display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-color-muted)' }}>
                    <span>6 Mt.</span><span>9</span><span>12</span><span>15</span><span>18</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16, fontSize: 13 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 14, height: 6, borderRadius: 3, background: 'var(--primary-50, #02FFA1)', opacity: 0.6 }}/>
                    <span><strong>{rangeText}</strong> · {t('result.timeline_realistic_label')}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 12, height: 12, borderRadius: 6, background: accent, border: '2px solid var(--global-background, #fff)', boxShadow: '0 0 0 1px ' + accent }}/>
                    <span>{t('result.timeline_wish_legend')}</span>
                  </div>
                </div>

                <div
                  className="va__assessment is-soft"
                  style={{
                    borderColor: accent,
                    background: `color-mix(in srgb, ${accent}, transparent 94%)`,
                  }}
                >
                  <div className="va__assessment-eyebrow" style={{ color: accent }}>
                    <i className={`fa-solid ${evalT.status === 'ok' ? 'fa-circle-check' : evalT.status === 'tight' ? 'fa-triangle-exclamation' : 'fa-circle-exclamation'}`}></i>
                    {t('result.timeline_cat')}
                  </div>
                  <h2 className="va__assessment-title">{t(`result.timeline_status_${evalT.status}_title`)}</h2>
                  <p className="va__assessment-text">
                    {evalT.status === 'ok'
                      ? t('result.timeline_status_ok_body')
                      : evalT.status === 'tight'
                        ? t('result.timeline_status_tight_body', { gap: evalT.gapMonths, target: tline.realisticMin })
                        : t('result.timeline_status_critical_body', { gap: evalT.gapMonths, target: tline.realisticMin })}
                  </p>
                </div>

              </div>
            );
          })()}

          {/* Drivers + open questions */}
          <div className="va__row3">
            <div>
              <h3 className="va__sect-title">{t('result.v4_drivers_title')}</h3>
              {drivers3.map((d, i) => (
                <div key={d.id} className="va__driver">
                  <span className="va__driver-rank">{i + 1}</span>
                  <span className="va__driver-label">{d.label}</span>
                  <span className="va__driver-pct">{d.pct}%</span>
                </div>
              ))}
              {drivers3.every(d => d.weight === 0) && (
                <div className="muted" style={{ fontSize: 13 }}>{t('result.v4_drivers_empty')}</div>
              )}
            </div>
            <div>
              <h3 className="va__sect-title">{t('result.v4_uncertainty_title')}</h3>
              {r.uncertainties.length > 0 ? (
                <ul className="va__uncertainty-list">
                  {r.uncertainties.slice(0, 4).map((u, i) => <li key={i}>{u}</li>)}
                </ul>
              ) : (
                <div className="muted" style={{ fontSize: 13 }}>{t('result.v4_uncertainty_empty')}</div>
              )}
            </div>
          </div>

          {/* Review (alle Angaben) — 3-card grid */}
          <div className="va__review">
            <div className="va__review-head">
              <h3 className="va__sect-title" style={{ margin: 0 }}>{t('result.v4_review_title')}</h3>
            </div>

            <div className="va__review-cards">
              {/* — Card 1 — Umfang & Funktionen */}
              <button type="button" className="va__rcard" onClick={() => goTo('p1')}>
                <div className="va__rcard-head">
                  <span className="va__rcard-num">1</span>
                  <span className="va__rcard-title">{t('result.v4_review_step1_title')}</span>
                  <span className="va__rcard-edit" aria-label={t('result.v4_review_aria_edit')}>
                    <i className="fa-solid fa-pen-to-square"></i>
                  </span>
                </div>
                <dl className="va__rcard-body">
                  <div className="va__rrow">
                    <dt>{t('result.review_modules')}</dt>
                    <dd>{moduleNames.length
                      ? <span className="va__chiprow">{moduleNames.map((n,i) => <span key={i} className="va__chip">{n}</span>)}</span>
                      : <span className="is-empty">{t('result.review_modules_none')}</span>}</dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_hilfs')}</dt>
                    <dd>{hilfsNames.length
                      ? <span className="va__chiprow">{hilfsNames.map((n,i) => <span key={i} className="va__chip va__chip--soft">{n}</span>)}</span>
                      : <span className="is-empty">{t('result.review_modules_none')}</span>}</dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_ki')}</dt>
                    <dd>{kiNames.length
                      ? <span className="va__chiprow">{kiNames.map((n,i) => <span key={i} className="va__chip">{n}</span>)}</span>
                      : <span className="is-empty">{t('result.review_modules_none')}</span>}</dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_stackcms')}</dt>
                    <dd>{stackcmsNames.length
                      ? <span className="va__chiprow">{stackcmsNames.map((n,i) => <span key={i} className="va__chip va__chip--soft">{n}</span>)}</span>
                      : <span className="is-empty">{t('result.review_modules_none')}</span>}</dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_custom')}</dt>
                    <dd>{t('result.review_custom_split', { core: p1.customCore, support: p1.customSupport })} · <span className="va__muted">{_cxLabel(p1.customComplexity)}</span></dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_lang_perf_audit')}</dt>
                    <dd>{_langLabel(p1.languages)} · {_performLabel(p1.performance)} · <span className="va__muted">{_auditLabel(p1.auditability)}</span></dd>
                  </div>
                </dl>
              </button>

              {/* — Card 2 — Technik & Daten */}
              <button type="button" className="va__rcard" onClick={() => goTo('p2')}>
                <div className="va__rcard-head">
                  <span className="va__rcard-num">2</span>
                  <span className="va__rcard-title">{t('result.v4_review_step2_title')}</span>
                  <span className="va__rcard-edit" aria-label={t('result.v4_review_aria_edit')}>
                    <i className="fa-solid fa-pen-to-square"></i>
                  </span>
                </div>
                <dl className="va__rcard-body">
                  <div className="va__rrow">
                    <dt>{t('result.review_integrations')}</dt>
                    <dd>{p2.integrations.length
                      ? t('result.review_integrations_count', { count: p2.integrations.length })
                      : <span className="is-empty">{t('result.review_integrations_none')}</span>}</dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_migration')}</dt>
                    <dd>{
                      p2.migration === 'yes' ? t('result.review_migration_yes', { count: p2.migrationStreams }) :
                      p2.migration === 'no' ? t('result.review_migration_no') :
                      p2.migration === 'unknown' ? t('result.review_migration_unknown') :
                      <span className="is-empty">{t('result.review_migration_open')}</span>
                    }</dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_sensitivity')}</dt>
                    <dd>{_sensLabel(p2.sensitivity)}</dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_users')}</dt>
                    <dd>{_userLabel(p2.activeUsers)} · <span className="va__muted">Peak {_peakLabel(p2.peakConcurrency)}</span></dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_tenant')}</dt>
                    <dd>{_tenantLabel(p2.tenantModel)}</dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.v4_review_security_compliance_label')}</dt>
                    <dd>{(p2.security.length + p2.compliance.length) === 0
                      ? <span className="is-empty">{t('result.v4_review_security_compliance_default')}</span>
                      : <span className="va__chiprow">
                          {p2.security.length > 0 && <span className="va__chip va__chip--soft"><i className="fa-solid fa-shield-halved"></i>{t('result.review_security_chip', { count: p2.security.length })}</span>}
                          {p2.compliance.length > 0 && <span className="va__chip va__chip--soft"><i className="fa-solid fa-scale-balanced"></i>{t('result.review_compliance_chip', { count: p2.compliance.length })}</span>}
                        </span>}</dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_residency')}</dt>
                    <dd>{_residencyLabel(p2.dataResidency)}</dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_availability')}</dt>
                    <dd>{_availLabel(p2.availability)}</dd>
                  </div>
                </dl>
              </button>

              {/* — Card 3 — Kosten */}
              <button type="button" className="va__rcard" onClick={() => goTo('p3')}>
                <div className="va__rcard-head">
                  <span className="va__rcard-num">3</span>
                  <span className="va__rcard-title">{t('result.v4_review_step3_title')}</span>
                  <span className="va__rcard-edit" aria-label={t('result.v4_review_aria_edit')}>
                    <i className="fa-solid fa-pen-to-square"></i>
                  </span>
                </div>
                <dl className="va__rcard-body">
                  <div className="va__rrow va__rrow--hero">
                    <dt>{t('result.review_build_budget')}</dt>
                    <dd className="va__hero-value">{fmtChf(r.chosenBudget)}</dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_downpayment')}</dt>
                    <dd>{t('result.review_downpayment_split', { pct: p3.downPayment, years: yearsLabel })}</dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_growth')}</dt>
                    <dd>{p3.growthBudget}<span className="va__muted">% = {fmtChf(r.yearlyGrowth)} / {t('result.review_per_year')}</span></dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_sla')}</dt>
                    <dd>{(() => {
                      const ids = Array.isArray(p3.sla) ? p3.sla : (p3.sla && p3.sla !== 'none' ? [p3.sla] : []);
                      if (!ids.length) return <span className="is-empty">{t('p3.sla_options.none.label')}</span>;
                      const label = ids.map(id => t(`p3.sla_options.${id}.label`)).join(' + ');
                      return t('result.review_sla_value', { label, amount: fmtChf(r.slaFee) });
                    })()}</dd>
                  </div>
                  <div className="va__rrow">
                    <dt>{t('result.review_service_options')}</dt>
                    <dd>{(() => {
                      const parts = [];
                      if (p3.seoChecks)                                            parts.push(t('p3.seo_cat'));
                      if (p3.dataProtection)                                       parts.push(t('p3.dataprotection_cat'));
                      if (p3.securityScanning && p3.securityScanning !== 'none')   parts.push(t('p3.secscan_cat') + ' · ' + t(`p3.secscan_options.${p3.securityScanning}.label`));
                      return parts.length
                        ? parts.join(' · ')
                        : <span className="is-empty">{t('result.review_modules_none')}</span>;
                    })()}</dd>
                  </div>
                </dl>
              </button>
            </div>
          </div>

          {/* Foot */}
          <div className="va__foot">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span>{t('result.v4_foot_config_code_label')}</span>
              <span className="va__code">{cfgCode}</span>
            </div>
          </div>

        </div>
      </div>

      {/* Beratungsoptionen — eigenständige Karte UNTERHALB des Übersichts-Shells.
          Beratung ist optional und projektunabhängig und fliesst bewusst NICHT
          in den TCO ein; die Summe wird nur innerhalb dieses Blocks ausgewiesen. */}
      <div className="block is-card" style={{ marginTop: 32 }}>
        <div className="block__head">
          <div>
            <span className="block__cat">{t('p3.block_advisory_cat')}</span>
            <h2 className="block__title">{t('p3.block_advisory_title')}</h2>
            <p className="block__lead">{t('p3.block_advisory_lead')}</p>
          </div>
        </div>

        <AdvisoryCard
          recommended
          cat={t('p3.advisory_visualize_cat')}
          title={t('p3.advisory_design_title')}
          lead={t('p3.advisory_design_lead')}
          value={p3.advisoryDesign}
          onChange={(v) => setP3({ advisoryDesign: v })}
          onDetails={() => openDrawer('advisory-design')}
          sizes={[
            { id: 'none', label: t('p3.advisory_size_none'),    pt: 0,  sub: t('p3.advisory_design_size.none.sub') },
            { id: 's',    label: t('p3.advisory_size.s.label'), pt: 5,  sub: t('p3.advisory_design_size.s.sub') },
            { id: 'm',    label: t('p3.advisory_size.m.label'), pt: 10, sub: t('p3.advisory_design_size.m.sub') },
            { id: 'l',    label: t('p3.advisory_size.l.label'), pt: 20, sub: t('p3.advisory_design_size.l.sub') },
          ]}
        />

        <AdvisoryCard
          cat={t('p3.advisory_alternative')}
          title={t('p3.advisory_spec_title')}
          lead={t('p3.advisory_spec_lead')}
          value={p3.advisorySpec}
          onChange={(v) => setP3({ advisorySpec: v })}
          onDetails={() => openDrawer('advisory-spec')}
          sizes={[
            { id: 'none', label: t('p3.advisory_size_none'),    pt: 0,  sub: t('p3.advisory_spec_size.none.sub') },
            { id: 's',    label: t('p3.advisory_size.s.label'), pt: 5,  sub: t('p3.advisory_spec_size.s.sub') },
            { id: 'm',    label: t('p3.advisory_size.m.label'), pt: 10, sub: t('p3.advisory_spec_size.m.sub') },
            { id: 'l',    label: t('p3.advisory_size.l.label'), pt: 20, sub: t('p3.advisory_spec_size.l.sub') },
          ]}
        />

        <AdvisoryCard
          cat={t('p3.advisory_prototype_cat')}
          title={t('p3.advisory_prototype_title')}
          lead={t('p3.advisory_prototype_lead')}
          value={p3.advisoryPrototype}
          onChange={(v) => setP3({ advisoryPrototype: v })}
          onDetails={() => openDrawer('advisory-prototype')}
          sizes={[
            { id: 'none', label: t('p3.advisory_size_none'),    pt: 0,  sub: t('p3.advisory_prototype_size.none.sub') },
            { id: 's',    label: t('p3.advisory_size.s.label'), pt: 10, sub: t('p3.advisory_prototype_size.s.sub') },
            { id: 'm',    label: t('p3.advisory_size.m.label'), pt: 20, sub: t('p3.advisory_prototype_size.m.sub') },
            { id: 'l',    label: t('p3.advisory_size.l.label'), pt: 30, sub: t('p3.advisory_prototype_size.l.sub') },
          ]}
        />

        {(r.advisoryCost > 0) && (
          <div className="cost-card cost-card--sum" style={{ marginTop: 16 }}>
            <div className="between">
              <div>
                <span className="cost-card__cat">{t('p3.advisory_sum_cat')}</span>
                <div className="cost-card__rate">{t('p3.advisory_sum_rate', { pt: r.advisoryPT })}</div>
              </div>
              <div className="cost-card__amount">{fmtChf(r.advisoryCost)}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export { Result };
